import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { actionCardsApi, calendarApi } from '@/utils/api';
import { useAuth, GMAIL_ENABLED } from '@/contexts/AuthContext';
import { Mail, Send, Loader2, Info, CalendarClock, Sparkles, ArrowUp, Wand2, Bookmark, Plus, X, Copy, ChevronDown, AppWindow, CalendarPlus, Download } from 'lucide-react';

function defaultFollowUpDate(timing) {
  const days = { same_day: 1, next_day: 1, two_days: 2, three_days: 3, next_week: 7 }[timing] ?? 3;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const QUICK_PROMPTS = [
  'Make it warmer', 'Make it shorter', 'More formal',
  'Emphasize next steps', 'Add a clear call to action', 'Sound more confident',
];

export default function EmailModal({ open, onClose, actionCard }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('compose');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState({ subject: '', body: '', to: '' });
  const [schedule, setSchedule] = useState(true);
  const [followUpDate, setFollowUpDate] = useState('');
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [refining, setRefining] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [versionName, setVersionName] = useState('');
  const chatEndRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && actionCard?.id) {
      setTab('compose');
      setEmail({ subject: '', body: '', to: actionCard?.contact_email || '' });
      setSchedule(true);
      setFollowUpDate(defaultFollowUpDate(user?.follow_up_timing));
      setChat([]);
      setChatInput('');
      setVersions((actionCard.email_drafts || []).map(d => ({
        id: d.id, name: d.name, subject: d.subject, body: d.body, by: d.created_by_name,
      })));
      setShowSave(false);
      setVersionName('');
      generateEmail();
    }
  }, [open, actionCard?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, refining]);

  const generateEmail = async () => {
    setLoading(true);
    try {
      const res = await actionCardsApi.generateEmail(actionCard.id, 'professional');
      setEmail(prev => ({ ...prev, subject: res.data.subject || '', body: res.data.body || '' }));
    } catch {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const refine = async (instruction) => {
    const text = (instruction || '').trim();
    if (!text || refining || loading) return;
    setChatInput('');
    const history = chat.map(m => ({ role: m.role, content: m.role === 'user' ? m.content : m.content }));
    setChat(prev => [...prev, { role: 'user', content: text }]);
    setRefining(true);
    try {
      const res = await actionCardsApi.refineEmail(actionCard.id, {
        subject: email.subject, body: email.body, instruction: text, history,
      });
      setEmail(prev => ({ ...prev, subject: res.data.subject ?? prev.subject, body: res.data.body ?? prev.body }));
      setChat(prev => [...prev, { role: 'assistant', content: res.data.reply || 'Updated the draft.' }]);
    } catch (err) {
      setChat(prev => [...prev, { role: 'assistant', content: "Sorry — I couldn't update the draft. Try rephrasing." }]);
      toast.error(err?.response?.data?.detail || 'Failed to refine email');
    } finally {
      setRefining(false);
    }
  };

  const [savingVersion, setSavingVersion] = useState(false);
  const saveVersion = async () => {
    if (!email.subject && !email.body) { toast.error('Nothing to save yet'); return; }
    if (versions.length >= 5) { toast.error('You can keep up to 5 versions'); return; }
    const name = versionName.trim() || `Draft ${versions.length + 1}`;
    setSavingVersion(true);
    try {
      const res = await actionCardsApi.addEmailDraft(actionCard.id, { name, subject: email.subject, body: email.body });
      const d = res.data;
      setVersions(prev => [...prev, { id: d.id, name: d.name, subject: d.subject, body: d.body, by: d.created_by_name }]);
      setVersionName('');
      setShowSave(false);
      toast.success(`Saved "${d.name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save version'); }
    finally { setSavingVersion(false); }
  };

  const removeVersion = async (id) => {
    setVersions(prev => prev.filter(x => x.id !== id));
    try { await actionCardsApi.deleteEmailDraft(actionCard.id, id); } catch { /* already removed locally */ }
  };

  const loadVersion = (v) => {
    setEmail(prev => ({ ...prev, subject: v.subject, body: v.body }));
    setTab('compose');
    toast.success(`Loaded "${v.name}"`);
  };

  const handleSend = async () => {
    if (!email.subject || !email.body) { toast.error('Subject and body required'); return; }
    if (schedule && !followUpDate) { toast.error('Pick a follow-up date'); return; }
    setSending(true);
    try {
      if (schedule) {
        const res = await actionCardsApi.sendAndSchedule(actionCard.id, { ...email, follow_up_date: followUpDate });
        toast.success(res?.data?.message || 'Email sent & follow-up scheduled!');
      } else {
        const res = await actionCardsApi.sendEmail(actionCard.id, email);
        toast.success(res?.data?.message || 'Email sent!');
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Whether Milk can send email on the user's behalf. Microsoft can (Mail.Send);
  // Google can only when gmail.send is enabled (deferred by REACT_APP_ENABLE_GMAIL
  // to avoid CASA). When it can't, we offer copy / open-in-Gmail fallbacks.
  const canSendEmail = !!user?.microsoft_services_connected || (GMAIL_ENABLED && !!user?.google_services_connected);

  const copyDraft = async () => {
    if (!email.subject && !email.body) { toast.error('Nothing to copy yet'); return; }
    const text = `Subject: ${email.subject}\n\n${email.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Draft copied to clipboard');
    } catch {
      toast.error('Could not copy — select the text and copy manually');
    }
  };

  // Open the draft in an external mail client, pre-filled. No send scope needed.
  const openInMailClient = (target) => {
    if (!email.subject && !email.body) { toast.error('Nothing to send yet'); return; }
    const to = encodeURIComponent(email.to || '');
    const su = encodeURIComponent(email.subject || '');
    const body = encodeURIComponent(email.body || '');
    let url;
    if (target === 'gmail') {
      url = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${su}&body=${body}`;
    } else if (target === 'outlook') {
      url = `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${su}&body=${body}`;
    } else {
      url = `mailto:${email.to || ''}?subject=${su}&body=${body}`;
    }
    window.open(url, target === 'default' ? '_self' : '_blank', 'noopener');
  };

  // ── No-OAuth calendar helpers (follow-up reminder) ──
  const savedReminderRef = useRef(false);
  const followUpTitle = () => `Follow up with ${actionCard?.contact_name || 'contact'}`;
  const followUpDesc = () => `Follow-up reminder from Milk: ${actionCard?.title || followUpTitle()}.`;

  // Persist to Milk once so it appears on the user's subscribed calendar feed.
  const persistReminder = async () => {
    if (savedReminderRef.current || !followUpDate) return;
    savedReminderRef.current = true;
    try {
      await calendarApi.createReminder({ title: followUpTitle(), date: followUpDate, description: followUpDesc(), card_id: actionCard?.id });
    } catch { savedReminderRef.current = false; }
  };

  const gcalAllDayRange = (d) => {
    const start = d.replace(/-/g, '');
    const next = new Date(d + 'T00:00:00Z');
    next.setUTCDate(next.getUTCDate() + 1);
    return `${start}/${next.toISOString().slice(0, 10).replace(/-/g, '')}`;
  };

  const addToGoogleCalendar = () => {
    if (!followUpDate) { toast.error('Pick a date first'); return; }
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(followUpTitle())}&dates=${gcalAllDayRange(followUpDate)}&details=${encodeURIComponent(followUpDesc())}`;
    window.open(url, '_blank', 'noopener');
    persistReminder();
    toast.success('Reminder saved to your Milk calendar');
  };

  const downloadIcs = () => {
    if (!followUpDate) { toast.error('Pick a date first'); return; }
    const dstart = followUpDate.replace(/-/g, '');
    const nx = new Date(followUpDate + 'T00:00:00Z'); nx.setUTCDate(nx.getUTCDate() + 1);
    const dend = nx.toISOString().slice(0, 10).replace(/-/g, '');
    const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const esc = (t) => String(t || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Milk//Follow-ups//EN', 'BEGIN:VEVENT',
      `UID:${Date.now()}@addmilk.ai`, `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${dstart}`, `DTEND;VALUE=DATE:${dend}`,
      `SUMMARY:${esc(followUpTitle())}`, `DESCRIPTION:${esc(followUpDesc())}`, 'STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'milk-follow-up.ics';
    a.click();
    URL.revokeObjectURL(a.href);
    persistReminder();
    toast.success('Reminder saved to your Milk calendar');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="email-modal" className="max-w-2xl bg-white border-border shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Mail size={18} className="text-primary" />
            Generate Follow-up Email
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            AI-generated email for {actionCard?.contact_name}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2" data-testid="email-tabs">
            <TabsTrigger value="compose" data-testid="email-tab-compose">Compose</TabsTrigger>
            <TabsTrigger value="refine" data-testid="email-tab-refine" className="gap-1.5">
              <Sparkles size={13} />Refine with AI
            </TabsTrigger>
          </TabsList>

          {/* ── Compose tab ── */}
          <TabsContent value="compose" className="space-y-4 py-2 mt-2">
            <div
              data-testid="email-send-mocked-note"
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs text-muted-foreground"
              style={{ background: 'rgba(105,148,198,0.06)', border: '1px solid rgba(105,148,198,0.18)' }}
            >
              <Info size={13} className="text-primary mt-0.5 shrink-0" />
              {canSendEmail ? (
                <span>Sent from your connected email account, so replies come straight back to you.</span>
              ) : (
                <span data-testid="email-fallback-note">Milk can't send on your behalf yet — use <strong>Send via</strong> to open this draft in Gmail, Outlook or your default mail app, ready to send.</span>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input
                value={email.to}
                onChange={e => setEmail(p => ({ ...p, to: e.target.value }))}
                placeholder={actionCard?.contact_email || 'recipient@email.com'}
                className="bg-secondary/30 border-border h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Subject</Label>
              {loading ? <Skeleton className="h-9 w-full" /> : (
                <Input
                  data-testid="email-subject-input"
                  value={email.subject}
                  onChange={e => setEmail(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Email subject"
                  className="bg-secondary/30 border-border h-9 text-sm"
                />
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Body</Label>
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3,4].map(i => <Skeleton key={i} className={`h-4 w-${['full','5/6','4/5','3/4'][i-1]}`} />)}
                </div>
              ) : (
                <Textarea
                  data-testid="email-body-textarea"
                  value={email.body}
                  onChange={e => setEmail(p => ({ ...p, body: e.target.value }))}
                  placeholder="Email body"
                  className="bg-secondary/30 border-border text-sm min-h-[200px] resize-none"
                  rows={8}
                />
              )}
            </div>

            {/* Saved draft versions — keep a couple (e.g. warm vs formal) and pick before sending */}
            {!loading && (
              <div className="rounded-xl border border-border p-3" data-testid="email-versions-section">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5"><Bookmark size={13} className="text-primary" />Saved versions</Label>
                  {!showSave && versions.length < 5 && (
                    <button onClick={() => { setShowSave(true); setVersionName(''); }} data-testid="email-save-version-toggle"
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                      <Plus size={12} />Save this draft
                    </button>
                  )}
                </div>
                {showSave && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input autoFocus value={versionName} onChange={e => setVersionName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveVersion(); } }}
                      placeholder='Name it — e.g. "Warm" or "Formal"' data-testid="email-version-name-input"
                      className="bg-secondary/30 border-border h-8 text-sm" />
                    <Button size="sm" onClick={saveVersion} disabled={savingVersion} data-testid="email-version-save-button"
                      className="h-8 text-xs" style={{ background: '#6994c6', color: 'white' }}>{savingVersion ? <Loader2 size={12} className="animate-spin" /> : 'Save'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowSave(false)} className="h-8 text-xs text-muted-foreground">Cancel</Button>
                  </div>
                )}
                {versions.length === 0 && !showSave ? (
                  <p className="text-xs text-muted-foreground mt-1.5">Save a couple of drafts (like a warm and a formal take) — they're kept on this card and visible to teammates you share it with.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="email-versions-list">
                    {versions.map(v => (
                      <span key={v.id} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-border bg-white text-xs" data-testid={`email-version-chip-${v.id}`}>
                        <button onClick={() => loadVersion(v)} className="font-medium text-foreground hover:text-primary" data-testid={`email-version-load-${v.id}`} title={v.by ? `Saved by ${v.by}` : undefined}>{v.name}</button>
                        <button onClick={() => removeVersion(v.id)} className="text-muted-foreground hover:text-destructive" data-testid={`email-version-remove-${v.id}`}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div
              data-testid="email-schedule-section"
              className="rounded-xl border border-border p-3.5"
              style={{ background: 'rgba(105,148,198,0.04)' }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <CalendarClock size={16} className="text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">Schedule next touch</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Add a follow-up reminder to your calendar — it also syncs to your subscribed Milk calendar.</p>
                  </div>
                </div>
                <Switch data-testid="email-schedule-toggle" checked={schedule} onCheckedChange={setSchedule} />
              </div>
              {schedule && (
                <div className="mt-3 pl-[26px] space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">Remind me on</Label>
                    <Input
                      type="date"
                      data-testid="email-followup-date-input"
                      value={followUpDate}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={e => { savedReminderRef.current = false; setFollowUpDate(e.target.value); }}
                      className="bg-white border-border h-9 text-sm w-[170px]"
                    />
                  </div>
                  {followUpDate && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button data-testid="email-add-gcal-button" variant="outline" size="sm" onClick={addToGoogleCalendar}
                        className="border-border text-xs bg-white h-8">
                        <CalendarPlus size={13} className="mr-1.5" />Add to Google Calendar
                      </Button>
                      <Button data-testid="email-download-ics-button" variant="outline" size="sm" onClick={downloadIcs}
                        className="border-border text-xs bg-white h-8">
                        <Download size={13} className="mr-1.5" />Download .ics
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Refine with AI tab ── */}
          <TabsContent value="refine" className="mt-2" data-testid="email-refine-tab">
            <div className="rounded-xl border border-border overflow-hidden" style={{ background: '#fbfaf6' }}>
              <div className="h-[290px] overflow-y-auto p-3 space-y-3" data-testid="email-chat-messages">
                {chat.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(105,148,198,0.14)', color: '#6994c6' }}>
                      <Wand2 size={18} />
                    </div>
                    <p className="text-sm font-medium text-foreground">Refine your email in chat</p>
                    <p className="text-xs text-muted-foreground mt-1">Ask me to adjust the tone, length, emphasis or content — the draft on the Compose tab updates live.</p>
                  </div>
                )}
                {chat.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`email-chat-msg-${m.role}`}>
                    <div className="max-w-[80%] px-3 py-2 rounded-2xl text-sm"
                      style={m.role === 'user'
                        ? { background: '#6994c6', color: '#fff', borderBottomRightRadius: 4 }
                        : { background: '#fff', color: '#212E40', border: '1px solid rgba(33,46,64,.1)', borderBottomLeftRadius: 4 }}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {refining && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-2xl text-sm flex items-center gap-2" style={{ background: '#fff', border: '1px solid rgba(33,46,64,.1)', color: '#8c94a0', borderBottomLeftRadius: 4 }}>
                      <Loader2 size={13} className="animate-spin" />Updating the draft…
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              <div className="border-t border-border p-2.5 bg-white">
                {chat.length === 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="email-quick-prompts">
                    {QUICK_PROMPTS.map(p => (
                      <button key={p} onClick={() => refine(p)} disabled={refining || loading}
                        data-testid={`email-quick-prompt-${p.replace(/ /g, '-').toLowerCase()}`}
                        className="text-xs font-medium px-2.5 h-7 rounded-full border border-border text-muted-foreground hover:bg-secondary/50 transition-colors disabled:opacity-50">
                        {p}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    data-testid="email-chat-input"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); refine(chatInput); } }}
                    placeholder="e.g. Make it shorter and mention the pricing timeline…"
                    className="bg-secondary/30 border-border text-sm resize-none min-h-[40px] max-h-[96px]"
                    rows={1}
                    disabled={refining || loading}
                  />
                  <Button
                    data-testid="email-chat-send"
                    size="icon"
                    onClick={() => refine(chatInput)}
                    disabled={refining || loading || !chatInput.trim()}
                    className="h-10 w-10 shrink-0"
                    style={{ background: '#6994c6', color: 'white' }}>
                    {refining ? <Loader2 size={15} className="animate-spin" /> : <ArrowUp size={16} />}
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">Changes apply to the draft — switch to <button className="font-medium text-primary underline underline-offset-2" onClick={() => setTab('compose')} data-testid="email-refine-goto-compose">Compose</button> to review &amp; send.</p>
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={generateEmail} disabled={loading || sending}
            className="text-muted-foreground hover:text-foreground text-xs">
            Regenerate
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={sending}
              className="border-border text-sm bg-white">
              Cancel
            </Button>
            {canSendEmail ? (
              <Button
                data-testid="email-send-button"
                size="sm"
                onClick={handleSend}
                disabled={loading || sending || !email.subject}
                className="text-sm active:scale-[0.98]"
                style={{ background: '#6994c6', color: 'white' }}
              >
                {sending ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Sending…</> : <><Send size={13} className="mr-1.5" />{schedule ? 'Send & schedule' : 'Send'}</>}
              </Button>
            ) : (
              <>
                <Button
                  data-testid="email-copy-draft-button"
                  variant="outline"
                  size="sm"
                  onClick={copyDraft}
                  disabled={loading || !email.subject}
                  className="border-border text-sm bg-white"
                >
                  <Copy size={13} className="mr-1.5" />Copy
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="email-send-via-button"
                      size="sm"
                      disabled={loading || !email.subject}
                      className="text-sm active:scale-[0.98]"
                      style={{ background: '#6994c6', color: 'white' }}
                    >
                      <Send size={13} className="mr-1.5" />Send via<ChevronDown size={14} className="ml-1 -mr-0.5 opacity-80" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52" data-testid="email-send-via-menu">
                    <DropdownMenuItem data-testid="email-send-gmail" onClick={() => openInMailClient('gmail')} className="gap-2 cursor-pointer">
                      <img src="https://www.google.com/images/icons/product/gmail-32.png" alt="" width={16} height={16} className="rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      Gmail
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="email-send-outlook" onClick={() => openInMailClient('outlook')} className="gap-2 cursor-pointer">
                      <Mail size={15} className="text-[#0072C6]" />
                      Outlook
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="email-send-default" onClick={() => openInMailClient('default')} className="gap-2 cursor-pointer">
                      <AppWindow size={15} className="text-muted-foreground" />
                      Default email app
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
